import { FC, useEffect, useState, useMemo } from 'react';
import { Grid } from '@mui/material';
import { pages } from '../../../utils/common';
import { ProfileViewProps } from './profile.interfaces';
import {
  useProfileTabMenuItems,
  useProfileAvailableRouteIds,
} from './profile.helpers';
import MainTitle from '../../components/mainTitle';
import TabGroup from '../../components/tabGroup';
import NavigationDrawer from '../../components/navigationDrawer/navigationDrawer';
import { Helmet } from 'react-helmet';
import { CONSTANTS } from '../../../utils/CONSTS';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useRbacState } from 'src/context/rbac.context';
import { useRbacSafeLandingPath } from 'src/hooks/useRbacSafeLandingPath';
import { hasAnySubmoduleReadPermission } from 'src/utils/rbacHelpers';

const ProfileContent: FC = () => {
  const { translateText } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);
  const { profileSubRouteId } = useParams();
  const navigate = useNavigate();
  const { rbac } = useRbacState();
  const safeLanding = useRbacSafeLandingPath();
  const availableRouteIds = useProfileAvailableRouteIds();
  const tabs = useProfileTabMenuItems(translateText);

  const hasAccess = useMemo(
    () => hasAnySubmoduleReadPermission(rbac, 'profile'),
    [rbac]
  );

  useEffect(() => {
    if (availableRouteIds.length === 0) return;

    const routeId =
      profileSubRouteId && profileSubRouteId !== 'undefined'
        ? profileSubRouteId
        : availableRouteIds[0];

    if (!availableRouteIds.includes(routeId)) {
      navigate(pages.profile.path + '/' + availableRouteIds[0], {
        replace: true,
      });
      return;
    }

    const tabIndex = availableRouteIds.indexOf(routeId);
    setSelectedTab(tabIndex >= 0 ? tabIndex : 0);

    if (!profileSubRouteId || profileSubRouteId === 'undefined') {
      navigate(pages.profile.path + '/' + routeId, { replace: true });
    }
  }, [profileSubRouteId, navigate, availableRouteIds]);

  if (!hasAccess) {
    return <Navigate to={safeLanding} replace />;
  }

  if (availableRouteIds.length === 0) {
    return <Navigate to={safeLanding} replace />;
  }

  const locationChangeHandler = (value: string | number) => {
    const tabIndex = value as number;
    setSelectedTab(tabIndex);
    if (tabIndex >= 0 && tabIndex < availableRouteIds.length) {
      navigate(pages.profile.path + '/' + availableRouteIds[tabIndex], {
        replace: true,
      });
    }
  };

  return (
    <Grid
      container
      data-testid="profile-container"
      sx={{ height: '100%', px: { xs: 0, sm: 0 } }}
    >
      <Grid
        item
        xs={12}
        sx={{
          pr: { xs: 0, sm: 4 },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1, sm: 0 },
          mb: { xs: 1, sm: 0 },
        }}
        data-testid="profile-view-header-container"
      >
        <MainTitle title={translateText('profile.title')} subTitle={selectedTab ? tabs[selectedTab].label : ''} />
      </Grid>
      <Grid item xs={12} sx={{ height: 'calc(100% - 64px)', overflow: 'hidden' }}>
        <TabGroup
          key={selectedTab}
          tabs={tabs}
          initiallySelected={selectedTab}
          onTabChange={(value) => locationChangeHandler(value)}
          dataTestIdPrefix="profile"
        />
      </Grid>
    </Grid>
  );
};

// Main component that provides navigation context
const ProfileView: FC<ProfileViewProps> = () => {
  return (
    <NavigationDrawer selectedPage={pages.profile.path} window={window ?? null}>
      <Helmet>
        <title>{CONSTANTS.title.profile}</title>
      </Helmet>
      <ProfileContent />
    </NavigationDrawer>
  );
};

export default ProfileView;
